import PopupAlert from "@pathwright/ui/src/components/alert/PopupAlert"
import PasteButton from "@pathwright/ui/src/components/button/PasteButton"
import {
  CopyResourceMutationVariables,
  useCopyResourceMutation
} from "../../api/generated"
import useClipboard from "../../clipboard/useClipboard"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { canAdministrate } from "../../user/permissions"

export function usePasteResource(
  variables?: Partial<CopyResourceMutationVariables>
) {
  const pwContext = usePathwrightContext()
  const clipboard = useClipboard({
    object_type: "resource"
  })
  const clip = clipboard.getClip()
  const resourceId = clip ? clip.object_id : null

  const enabled = Boolean(
    // Can only paste resource when clip with resource_id.
    resourceId &&
      // Ignore perms if no clip data exists.
      (!clip?.data ||
        // Ignore perms if no school_id exists in clip data.
        !clip.data.school_id ||
        // Assume user can always paste resource to same school.
        clip.data.school_id === pwContext.school.id ||
        // Only admins can paste resource to different school.
        (clip.data.user_is_admin_of_school && canAdministrate(pwContext)))
  )

  const [mutation, mutationState] = useCopyResourceMutation({
    variables: {
      id: parseInt(resourceId!),
      ...variables
    },
    refetchQueries: ["LibraryResources", "UserResources"]
  })

  const onPaste = async () => {
    await mutation()
    clipboard.clear()
  }

  const onCancel = () => {
    clipboard.clear()
  }

  return {
    enabled,
    clip,
    onPaste,
    onCancel,
    mutation,
    ...mutationState
  }
}

// Handles copying a resource from the clipboard. The resource in the clipboard
// can be from the current school or from any other school, though permission
// checks will be applied on the backend when attempting the resource copy.
const PasteResource = () => {
  const { enabled, clip, onPaste, onCancel, error } = usePasteResource()

  return (
    <>
      {!!error && <PopupAlert error={error} />}
      {enabled && (
        <PasteButton
          onPaste={onPaste}
          onCancel={onCancel}
          clip={clip}
          styleMode="dark"
          tooltipProps={{ placement: "left" }}
        />
      )}
    </>
  )
}

PasteResource.displayName = "PasteResource"

export default PasteResource
